import { z } from 'zod';
/**
 * Definición de un tracker.
 *
 * Un tracker no se programa: se declara. La pharma clona una plantilla de la
 * biblioteca clínica y edita esta estructura desde la consola — qué ítems quedan,
 * con qué escalas, con qué textos, cada cuánto. El front la renderiza y la API la
 * valida, ambos leyendo el mismo objeto.
 *
 * Todo lo que agregues acá tiene que poder renderizarse sin código nuevo. Si un
 * pedido de una pharma no entra en este schema, la respuesta correcta es extender
 * el schema para todas, no hacer una excepción para una.
 */
export const trackerCategorySchema = z.enum([
    'SYMPTOMS', // síntomas y eventos adversos
    'NUTRITION', // peso, alimentación, hidratación
    'TREATMENT', // adherencia, ciclos, institución
    'WELLBEING', // ánimo, sueño, actividad
]);
export const fieldTypeSchema = z.enum([
    'scale', // entero acotado, se renderiza como slider con anclas en los extremos
    'number', // numérico libre con unidad
    'boolean',
    'choice', // una opción
    'multichoice', // varias opciones
    'text',
    'time',
    'date',
    'bodymap', // selección de zonas del cuerpo (diario de dolor)
]);
export const fieldOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    /** Peso del ítem cuando el tracker calcula un puntaje (PHQ-9, GAD-7, PRO-CTCAE). */
    score: z.number().optional(),
    /** Color opcional para chips y gráficos. */
    color: z.string().optional(),
});
export const trackerFieldSchema = z.object({
    key: z
        .string()
        .regex(/^[a-z][a-z0-9_]*$/, 'La clave debe ser snake_case y empezar con letra'),
    label: z.string().min(1),
    help: z.string().optional(),
    type: fieldTypeSchema,
    required: z.boolean().default(false),
    /** Unidad mostrada junto al valor: kg, cm, ml, mg, °C, hs. */
    unit: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
    /**
     * Texto de los extremos de una escala. En instrumentos clínicos los anclajes
     * son parte del instrumento: "0 = sin dolor / 10 = el peor dolor imaginable"
     * no es decoración, cambia lo que el paciente responde.
     */
    anchors: z.object({ min: z.string(), max: z.string() }).optional(),
    options: z.array(fieldOptionSchema).optional(),
    /**
     * Nombre con el que este campo aparece en `metrics` y, por lo tanto, el que se
     * usa en las reglas de alerta. Por defecto es `key`. Existe para que la pharma
     * pueda renombrar la etiqueta visible sin romper las reglas que ya escribió.
     */
    metric: z.string().optional(),
    /** Mostrar el campo solo si otro campo tiene cierto valor. */
    showIf: z
        .object({
        field: z.string(),
        equals: z.union([z.string(), z.number(), z.boolean()]),
    })
        .optional(),
    placeholder: z.string().optional(),
});
export const trackerSectionSchema = z.object({
    key: z.string(),
    title: z.string().optional(),
    description: z.string().optional(),
    fields: z.array(trackerFieldSchema).min(1),
});
/**
 * Puntaje derivado. Muchos instrumentos validados no se leen ítem por ítem sino
 * por su total (PHQ-9 suma 0-27) y por la banda en la que cae ese total.
 */
export const trackerScoreSchema = z.object({
    key: z.string(),
    label: z.string(),
    method: z.enum(['sum', 'mean', 'max']),
    /** Claves de campo que entran en el puntaje. Vacío = todos los numéricos. */
    fields: z.array(z.string()).default([]),
    /** Bandas de interpretación, evaluadas en orden por `upTo` ascendente. */
    bands: z
        .array(z.object({
        upTo: z.number(),
        label: z.string(),
        severity: z.enum(['INFO', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL']).default('INFO'),
    }))
        .default([]),
});
export const trackerFrequencySchema = z.enum([
    'DAILY',
    'WEEKLY',
    'BIWEEKLY',
    'MONTHLY',
    'ON_DEMAND',
    'CYCLE_DAY', // atado al calendario de ciclos de tratamiento
]);
export const trackerDefinitionSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    name: z.string().min(1),
    shortName: z.string().optional(),
    description: z.string().optional(),
    /** Nombre de icono de lucide-react. */
    icon: z.string().default('activity'),
    category: trackerCategorySchema,
    /** Atribución del instrumento. Vacío para trackers propios de la pharma. */
    instrument: z
        .object({
        name: z.string(),
        source: z.string(),
        license: z.enum(['public-domain', 'free-with-attribution', 'licensed', 'proprietary']),
        notes: z.string().optional(),
    })
        .optional(),
    sections: z.array(trackerSectionSchema).min(1),
    scores: z.array(trackerScoreSchema).default([]),
    frequency: trackerFrequencySchema.default('ON_DEMAND'),
    /** Hora sugerida del recordatorio, HH:mm en zona del tenant. */
    reminderTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    /** Texto que ve el paciente al terminar de cargar. */
    completionMessage: z.string().optional(),
    /**
     * Métricas de este tracker que se grafican en el historial del paciente y en
     * el dashboard de la pharma. Si está vacío se grafican todas las numéricas.
     */
    chartMetrics: z.array(z.string()).default([]),
});
// ── Utilidades sobre la definición ───────────────────────────────────────────
export function allFields(def) {
    return def.sections.flatMap((s) => s.fields);
}
export function findField(def, key) {
    return allFields(def).find((f) => f.key === key);
}
/** Nombre de la métrica de un campo: el override si existe, si no la clave. */
export function metricNameOf(field) {
    return field.metric ?? field.key;
}
/**
 * Construye un validador zod para los datos de una entrada, derivado de la
 * definición. La API valida con esto, así una pharma no puede recibir datos que
 * no encajan con el tracker que ella misma configuró.
 */
export function buildEntryValidator(def) {
    const shape = {};
    for (const field of allFields(def)) {
        let schema;
        switch (field.type) {
            case 'scale':
            case 'number': {
                let n = z.number();
                if (field.min !== undefined)
                    n = n.min(field.min);
                if (field.max !== undefined)
                    n = n.max(field.max);
                schema = n;
                break;
            }
            case 'boolean':
                schema = z.boolean();
                break;
            case 'choice': {
                const values = (field.options ?? []).map((o) => o.value);
                schema = values.length > 0 ? z.enum(values) : z.string();
                break;
            }
            case 'multichoice': {
                const values = (field.options ?? []).map((o) => o.value);
                schema =
                    values.length > 0
                        ? z.array(z.enum(values))
                        : z.array(z.string());
                break;
            }
            case 'bodymap':
                schema = z.array(z.string());
                break;
            case 'date':
            case 'time':
            case 'text':
                schema = z.string();
                break;
        }
        // Un campo condicional nunca puede ser obligatorio a nivel schema: si su
        // condición no se cumple el paciente jamás lo vio, y exigirlo dejaría la
        // entrada imposible de guardar.
        shape[field.key] = field.required && !field.showIf ? schema : schema.optional().nullable();
    }
    return z.object(shape).passthrough();
}
/** ¿Corresponde mostrar este campo dado lo cargado hasta ahora? */
export function isFieldVisible(field, data) {
    if (!field.showIf)
        return true;
    return data[field.showIf.field] === field.showIf.equals;
}
/**
 * Normaliza una entrada a métricas numéricas.
 *
 * Es la pieza que conecta la carga del paciente con los gráficos y las reglas de
 * alerta: nada aguas abajo vuelve a mirar el JSON crudo. Un booleano vale 1/0, una
 * opción vale su `score` si lo tiene, una multiselección aporta su cantidad, y los
 * puntajes declarados se calculan y se agregan al resultado.
 */
export function computeMetrics(def, data) {
    const metrics = {};
    for (const field of allFields(def)) {
        const raw = data[field.key];
        if (raw === undefined || raw === null || raw === '')
            continue;
        const name = metricNameOf(field);
        switch (field.type) {
            case 'scale':
            case 'number':
                if (typeof raw === 'number' && Number.isFinite(raw))
                    metrics[name] = raw;
                break;
            case 'boolean':
                metrics[name] = raw === true ? 1 : 0;
                break;
            case 'choice': {
                const option = field.options?.find((o) => o.value === raw);
                if (option?.score !== undefined)
                    metrics[name] = option.score;
                break;
            }
            case 'multichoice': {
                if (!Array.isArray(raw))
                    break;
                metrics[`${name}_count`] = raw.length;
                // Si las opciones tienen puntaje, el total también sirve como métrica.
                const scored = raw
                    .map((v) => field.options?.find((o) => o.value === v)?.score)
                    .filter((s) => typeof s === 'number');
                if (scored.length > 0)
                    metrics[name] = scored.reduce((a, b) => a + b, 0);
                break;
            }
            case 'bodymap':
                if (Array.isArray(raw))
                    metrics[`${name}_count`] = raw.length;
                break;
            case 'text':
            case 'date':
            case 'time':
                // No aportan métrica numérica.
                break;
        }
    }
    for (const score of def.scores) {
        const keys = score.fields.length > 0
            ? score.fields
            : allFields(def)
                .filter((f) => f.type === 'scale' || f.type === 'number' || f.type === 'choice')
                .map((f) => f.key);
        const values = keys
            .map((k) => {
            const field = findField(def, k);
            return field ? metrics[metricNameOf(field)] : undefined;
        })
            .filter((v) => typeof v === 'number');
        if (values.length === 0)
            continue;
        let value;
        switch (score.method) {
            case 'sum':
                value = values.reduce((a, b) => a + b, 0);
                break;
            case 'mean':
                value = values.reduce((a, b) => a + b, 0) / values.length;
                break;
            case 'max':
                value = Math.max(...values);
                break;
        }
        metrics[score.key] = Math.round(value * 100) / 100;
    }
    return metrics;
}
/** Banda de interpretación en la que cae un puntaje. */
export function interpretScore(score, value) {
    const bands = [...score.bands].sort((a, b) => a.upTo - b.upTo);
    for (const band of bands) {
        if (value <= band.upTo)
            return { label: band.label, severity: band.severity };
    }
    const last = bands[bands.length - 1];
    return last ? { label: last.label, severity: last.severity } : null;
}
/**
 * Métricas que expone un tracker, con su etiqueta. Alimenta el desplegable del
 * editor de reglas de alerta: la pharma elige de una lista en vez de tipear el
 * nombre de una métrica y equivocarse en silencio.
 */
/**
 * Convierte el enunciado de una pregunta en el nombre corto del dato.
 *
 * Las preguntas de un instrumento clínico están escritas para el paciente:
 * "¿Cuántas deposiciones líquidas tuviste por día, como máximo?". Puesto dentro
 * de la condición de una alerta, eso se lee como si le estuviéramos preguntando
 * algo al equipo. Lo que hace falta ahí es el nombre de lo que se mide.
 *
 * Se corta en la primera coma o paréntesis y se saca la fórmula interrogativa
 * del principio, que es lo que sobra. Cuando no hay nada que recortar, devuelve
 * la etiqueta tal cual: es preferible una etiqueta larga a una mutilada.
 */
export function shortMetricLabel(label) {
    const withoutQuestion = label
        .replace(/^[¿¡]/, '')
        .replace(/[?!]\s*$/, '')
        .split(/[,(]/)[0]
        .trim();
    const simplified = withoutQuestion.replace(/^(cuántas?|cuánto|cuántos|qué tanto|con qué frecuencia|en qué medida)\s+/i, '');
    const clean = (simplified || withoutQuestion).trim();
    if (!clean)
        return label;
    return clean.charAt(0).toUpperCase() + clean.slice(1);
}
export function describeMetrics(def) {
    const out = [];
    for (const field of allFields(def)) {
        const name = metricNameOf(field);
        switch (field.type) {
            case 'scale':
            case 'number':
                out.push({ metric: name, label: shortMetricLabel(field.label), unit: field.unit });
                break;
            case 'boolean':
                out.push({ metric: name, label: shortMetricLabel(field.label) });
                break;
            case 'choice':
                if (field.options?.some((o) => o.score !== undefined)) {
                    out.push({ metric: name, label: shortMetricLabel(field.label) });
                }
                break;
            case 'multichoice':
            case 'bodymap':
                out.push({ metric: `${name}_count`, label: `${shortMetricLabel(field.label)} (cantidad)` });
                break;
            default:
                break;
        }
    }
    for (const score of def.scores) {
        out.push({ metric: score.key, label: `${score.label} (puntaje)` });
    }
    return out;
}
//# sourceMappingURL=schema.js.map